export class ModernPlayButton extends Phaser.GameObjects.Container {
    private background: Phaser.GameObjects.Graphics;
    private icon: Phaser.GameObjects.Graphics;
    private text: Phaser.GameObjects.Text;
    private particles: Phaser.GameObjects.Graphics[] = [];
    private isPressed: boolean = false;
    private pulseAnimation: Phaser.Tweens.Tween;
    private glowAnimation: Phaser.Tweens.Tween;
    
    constructor(scene: Phaser.Scene, x: number, y: number) {
        super(scene, x, y);
        scene.add.existing(this);
        
        this.createButton();
        this.setupInteractivity();
        this.addAnimations();
        this.setScale(0);
        this.setDepth(20);
    }
    
    private createButton() {
        // Create main button background with gradient
        this.background = this.scene.add.graphics();
        
        // Create outer glow effect
        for (let i = 5; i > 0; i--) {
            const alpha = 0.08 - (i * 0.012);
            const size = 90 + (i * 10);
            this.background.fillStyle(0x00ff88, alpha);
            this.background.fillCircle(0, 0, size);
        }
        
        // Main button gradient background
        this.background.fillGradientStyle(0x00ff88, 0x00ff88, 0x00cc66, 0x00cc66, 1);
        this.background.fillCircle(0, 0, 80);
        
        // Inner gradient for depth
        this.background.fillGradientStyle(0x00ffaa, 0x00ffaa, 0x00ff88, 0x00ff88, 0.3);
        this.background.fillCircle(0, -20, 60);
        
        // Dark edge for definition
        this.background.lineStyle(3, 0x008844, 1);
        this.background.strokeCircle(0, 0, 80);
        
        // Inner highlight ring
        this.background.lineStyle(2, 0xaaffdd, 0.7);
        this.background.strokeCircle(0, 0, 76);
        
        this.add(this.background);
        
        // Create play icon (modern triangular play button)
        this.icon = this.scene.add.graphics();
        this.createPlayIcon();
        this.add(this.icon);
        
        // Add "PLAY" text below the icon
        this.text = this.scene.add.text(0, 35, 'PLAY', {
            fontSize: '24px',
            fontFamily: 'Luckiest Guy',
            color: '#ffffff',
            stroke: '#006633',
            strokeThickness: 4,
            shadow: {
                offsetX: 2,
                offsetY: 2,
                color: '#003322',
                blur: 4,
                fill: true,
                stroke: true
            }
        });
        this.text.setOrigin(0.5);
        this.add(this.text);
        
        // Create floating particles around button
        this.createFloatingParticles();
    }
    
    private createPlayIcon() {
        // Clear previous icon
        this.icon.clear();
        
        // Create a modern play triangle with gradient effect
        const iconSize = this.isPressed ? 25 : 30;
        const xOffset = this.isPressed ? 3 : 5; // Offset to center the triangle
        
        // Shadow for the play icon
        this.icon.fillStyle(0x003322, 0.3);
        this.icon.fillTriangle(
            -iconSize + xOffset + 3, -iconSize + 3,
            -iconSize + xOffset + 3, iconSize + 3,
            iconSize + xOffset + 3, 3
        );
        
        // Main play triangle
        this.icon.fillStyle(0xffffff, 1);
        this.icon.fillTriangle(
            -iconSize + xOffset, -iconSize,
            -iconSize + xOffset, iconSize,
            iconSize + xOffset, 0
        );
        
        // Inner highlight for 3D effect
        this.icon.fillStyle(0xffffff, 0.3);
        this.icon.fillTriangle(
            -iconSize + xOffset + 5, -iconSize + 10,
            -iconSize + xOffset + 5, 0,
            0 + xOffset, -5
        );
    }
    
    private createFloatingParticles() {
        // Create small floating orbs around the button
        for (let i = 0; i < 8; i++) {
            const particle = this.scene.add.graphics();
            const angle = (i / 8) * Math.PI * 2;
            const radius = 100;
            
            particle.x = Math.cos(angle) * radius;
            particle.y = Math.sin(angle) * radius;
            
            // Create gradient orb
            particle.fillStyle(0x00ff88, 0.3);
            particle.fillCircle(0, 0, 4);
            particle.fillStyle(0xffffff, 0.8);
            particle.fillCircle(-1, -1, 1);
            
            this.add(particle);
            this.particles.push(particle);
            
            // Animate particles orbiting
            this.scene.tweens.add({
                targets: particle,
                x: Math.cos(angle + Math.PI) * radius,
                y: Math.sin(angle + Math.PI) * radius,
                duration: 4000 + (i * 200),
                ease: 'Sine.inOut',
                yoyo: true,
                repeat: -1,
                delay: i * 100
            });
            
            // Fade in and out
            this.scene.tweens.add({
                targets: particle,
                alpha: 0.1,
                duration: 2000,
                ease: 'Sine.inOut',
                yoyo: true,
                repeat: -1,
                delay: i * 150
            });
        }
    }
    
    private setupInteractivity() {
        // Make the button interactive
        this.setSize(160, 160);
        this.setInteractive();
        
        // Hover effect - more subtle
        this.on('pointerover', () => {
            this.scene.tweens.add({
                targets: this,
                scale: 1.02,
                duration: 200,
                ease: 'Sine.out'
            });
            
            // Brighten the button
            this.background.clear();
            for (let i = 5; i > 0; i--) {
                const alpha = 0.12 - (i * 0.015);
                const size = 90 + (i * 12);
                this.background.fillStyle(0x00ffaa, alpha);
                this.background.fillCircle(0, 0, size);
            }
            this.background.fillGradientStyle(0x00ffaa, 0x00ffaa, 0x00dd77, 0x00dd77, 1);
            this.background.fillCircle(0, 0, 80);
            this.background.fillGradientStyle(0x00ffcc, 0x00ffcc, 0x00ffaa, 0x00ffaa, 0.4);
            this.background.fillCircle(0, -20, 60);
            this.background.lineStyle(3, 0x009955, 1);
            this.background.strokeCircle(0, 0, 80);
            this.background.lineStyle(2, 0xccffee, 0.8);
            this.background.strokeCircle(0, 0, 76);
            
            // Speed up particle animation
            this.particles.forEach(particle => {
                this.scene.tweens.getTweensOf(particle).forEach(tween => {
                    tween.timeScale = 2;
                });
            });
        });
        
        // Hover out effect
        this.on('pointerout', () => {
            if (!this.isPressed) {
                this.scene.tweens.add({
                    targets: this,
                    scale: 1,
                    duration: 200,
                    ease: 'Back.out'
                });
                
                // Return to normal color
                this.background.clear();
                for (let i = 5; i > 0; i--) {
                    const alpha = 0.08 - (i * 0.012);
                    const size = 90 + (i * 10);
                    this.background.fillStyle(0x00ff88, alpha);
                    this.background.fillCircle(0, 0, size);
                }
                this.background.fillGradientStyle(0x00ff88, 0x00ff88, 0x00cc66, 0x00cc66, 1);
                this.background.fillCircle(0, 0, 80);
                this.background.fillGradientStyle(0x00ffaa, 0x00ffaa, 0x00ff88, 0x00ff88, 0.3);
                this.background.fillCircle(0, -20, 60);
                this.background.lineStyle(3, 0x008844, 1);
                this.background.strokeCircle(0, 0, 80);
                this.background.lineStyle(2, 0xaaffdd, 0.7);
                this.background.strokeCircle(0, 0, 76);
                
                // Slow down particle animation
                this.particles.forEach(particle => {
                    this.scene.tweens.getTweensOf(particle).forEach(tween => {
                        tween.timeScale = 1;
                    });
                });
            }
        });
        
        // Press effect - more subtle
        this.on('pointerdown', () => {
            this.isPressed = true;
            this.setScale(0.98);
            this.createPlayIcon();
            
            // Create ripple effect
            this.createRippleEffect();
        });
        
        // Release effect
        this.on('pointerup', () => {
            this.isPressed = false;
            this.setScale(1);
            this.createPlayIcon();
        });
    }
    
    private createRippleEffect() {
        const ripple = this.scene.add.graphics();
        ripple.x = this.x;
        ripple.y = this.y;
        ripple.lineStyle(3, 0x00ff88, 1);
        ripple.strokeCircle(0, 0, 80);
        ripple.setDepth(this.depth - 1);
        
        this.scene.tweens.add({
            targets: ripple,
            scaleX: 2,
            scaleY: 2,
            alpha: 0,
            duration: 600,
            ease: 'Power2',
            onComplete: () => {
                ripple.destroy();
            }
        });
    }
    
    private addAnimations() {
        // Entrance animation
        this.scene.tweens.add({
            targets: this,
            scale: 1,
            duration: 600,
            ease: 'Back.out',
            delay: 400,
            onComplete: () => {
                // Start pulse animation only after entrance animation completes
                // Pulse between normal size (1) and slightly smaller (0.97)
                this.pulseAnimation = this.scene.tweens.add({
                    targets: this,
                    scaleX: 0.97,
                    scaleY: 0.97,
                    duration: 2500,
                    ease: 'Sine.inOut',
                    yoyo: true,
                    repeat: -1
                });
            }
        });
        
        // Gentle rotation for the particles container
        this.scene.tweens.add({
            targets: this.particles,
            angle: '+=360',
            duration: 20000,
            ease: 'Linear',
            repeat: -1
        });
    }
    
    public animatePress(callback: () => void) {
        // Create a subtle press animation
        this.scene.tweens.add({
            targets: this,
            scale: 0.95,
            duration: 150,
            ease: 'Power2',
            yoyo: true,
            onComplete: () => {
                // Create explosion of particles
                this.createExplosionEffect();
                callback();
            }
        });
    }
    
    private createExplosionEffect() {
        for (let i = 0; i < 12; i++) {
            const particle = this.scene.add.graphics();
            particle.x = this.x;
            particle.y = this.y;
            
            particle.fillStyle(0x00ff88, 0.8);
            particle.fillCircle(0, 0, 3);
            
            const angle = (i / 12) * Math.PI * 2;
            const distance = 200;
            
            this.scene.tweens.add({
                targets: particle,
                x: this.x + Math.cos(angle) * distance,
                y: this.y + Math.sin(angle) * distance,
                alpha: 0,
                duration: 800,
                ease: 'Power2',
                onComplete: () => {
                    particle.destroy();
                }
            });
        }
    }
    
    public destroy() {
        if (this.pulseAnimation) {
            this.pulseAnimation.stop();
        }
        if (this.glowAnimation) {
            this.glowAnimation.stop();
        }
        super.destroy();
    }
}
